'use strict';

const { queryFirst, queryAll, addClass, removeClass, hasClass } = require('../domUtil');
const { breakpoints } = require('../constants');
const {
    CLASS_PRODUCT_TILE_CONTAINER,
    SELECTOR_PRODUCT_TILE_CONTAINER,
    SELECTOR_PRODUCT_GRID
} = require('../productTiles/productTileConstants');

const GRID_COLUMNS = {
    [breakpoints.lg]: 3,
    [breakpoints.md]: 2,
    [breakpoints.xs]: 2
};
const SELECTOR_SLOT = '.category-marketing-slot';
const CLASS_ROW_START = 'm-start';
const CLASS_GRID_INITIALIZED = 'm-initialized';
const CLASS_SINGLE = 'm-single';
const CLASS_DOUBLE = 'm-double';
const CLASS_ALIGN_LEFT = 'm-left';
// m-center is legacy content saved before the centered option was split by mobile placement
const CLASS_ALIGN_CENTER = 'm-center';
const CLASS_ALIGN_CENTER_RIGHT = 'm-center-right';
const CLASS_ALIGN_CENTER_LEFT = 'm-center-left';
const CLASS_ALIGN_RIGHT = 'm-right';
const SELECTOR_AFTER_GRID_SOURCE = '.js-plp-after-grid';
const SELECTOR_GRID_FOOTER = '.grid-footer';

let gridState = {
    width: 0,
    columns: 0
};

/** @type {HTMLElement[]} clones from .js-plp-after-grid currently in the product grid */
let injectedAfterGridNodes = [];

/**
 * Gets column count for medium grid layout.
 * @param {number} width
 * @returns {number}
 */
function getColumnsByBreakpoint(width) {
    if (width >= breakpoints.lg) {
        return GRID_COLUMNS[breakpoints.lg];
    }
    if (width >= breakpoints.md) {
        return GRID_COLUMNS[breakpoints.md];
    }
    return GRID_COLUMNS[breakpoints.xs];
}

/**
 * Updates and caches the grid state (width and columns).
 * @param {HTMLElement} gridElContainer
 * @returns {{ width: number, columns: number }}
 */
function updateGridState(gridElContainer) {
    const currentWidth = window.innerWidth;

    if (gridState.width === currentWidth) {
        return gridState;
    }

    const columns = getColumnsByBreakpoint(currentWidth);

    gridState = {
        width: currentWidth,
        columns: columns
    };

    return gridState;
}

/**
 * Column span for a slot — mirrors _productGrid.scss grid-column rules.
 * @param {HTMLElement} slotEl
 * @param {number} columns
 * @returns {number}
 */
function getSlotColumnSpan(slotEl, columns) {
    if (hasClass(slotEl, CLASS_SINGLE)) {
        return 1;
    }
    if (hasClass(slotEl, CLASS_DOUBLE)) {
        return Math.min(2, columns);
    }
    return columns;
}

/**
 * Starting grid column (1-based) for a slot.
 * @param {HTMLElement} slotEl
 * @param {number} columns
 * @returns {number}
 */
function getSlotStartColumn(slotEl, columns) {
    if (!hasClass(slotEl, CLASS_SINGLE) && !hasClass(slotEl, CLASS_DOUBLE)) {
        return 1;
    }

    if (hasClass(slotEl, CLASS_SINGLE)) {
        if (hasClass(slotEl, CLASS_ALIGN_CENTER_LEFT)) {
            return columns === 3 ? 2 : 1;
        }
        if (hasClass(slotEl, CLASS_ALIGN_CENTER) || hasClass(slotEl, CLASS_ALIGN_CENTER_RIGHT)) {
            return 2;
        }
        if (hasClass(slotEl, CLASS_ALIGN_RIGHT)) {
            return columns === 3 ? 3 : 2;
        }
        return 1;
    }

    if (hasClass(slotEl, CLASS_ALIGN_LEFT)) {
        return 1;
    }
    return columns === 3 ? 2 : 1;
}

/**
 * Reads explicit grid-row from inline style (e.g. "grid-row:3" or "3").
 * @param {HTMLElement} el
 * @returns {number|null}
 */
function getExplicitGridRow(el) {
    const rowStyle = el.style.gridRow;
    if (!rowStyle) {
        return null;
    }
    const match = rowStyle.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
}

/**
 * @param {Map<string, boolean>} occupancy
 * @param {number} row
 * @param {number} colStart
 * @param {number} colSpan
 */
function markOccupied(occupancy, row, colStart, colSpan) {
    for (let col = colStart; col < colStart + colSpan; col++) {
        occupancy.set(`${row}:${col}`, true);
    }
}

/**
 * Next free cell in row-major order starting from cursor.
 * @param {Map<string, boolean>} occupancy
 * @param {number} columns
 * @param {number} startRow
 * @param {number} startCol
 * @returns {{ row: number, col: number }}
 */
function findNextFreeCell(occupancy, columns, startRow, startCol) {
    let row = startRow;
    let col = startCol;

    while (occupancy.get(`${row}:${col}`)) {
        col++;
        if (col > columns) {
            col = 1;
            row++;
        }
    }

    return { row, col };
}

/**
 * Compute visual grid positions: slots pinned by grid-row, tiles fill remaining cells.
 * Matches CSS grid when interleaved DOM is laid out in source order.
 * @param {HTMLElement[]} tiles
 * @param {{ slot: HTMLElement, order: number }[]} slots
 * @param {number} columns
 * @returns {{ el: HTMLElement, row: number, col: number, order: number }[]}
 */
function computeVisualPlacements(tiles, slots, columns) {
    const occupancy = new Map();
    const placements = [];

    const sortedSlots = [...slots].sort((a, b) => {
        const rowA = getExplicitGridRow(a.slot) || parseInt(a.slot.dataset.row, 10) || 0;
        const rowB = getExplicitGridRow(b.slot) || parseInt(b.slot.dataset.row, 10) || 0;
        return rowA - rowB || a.order - b.order;
    });

    sortedSlots.forEach(slotEntry => {
        const row = getExplicitGridRow(slotEntry.slot) || parseInt(slotEntry.slot.dataset.row, 10);
        if (!Number.isFinite(row) || row <= 0) {
            return;
        }

        const colStart = getSlotStartColumn(slotEntry.slot, columns);
        const colSpan = getSlotColumnSpan(slotEntry.slot, columns);
        markOccupied(occupancy, row, colStart, colSpan);
        placements.push({
            el: slotEntry.slot,
            row,
            col: colStart,
            order: slotEntry.order
        });
    });

    let autoRow = 1;
    let autoCol = 1;

    tiles.forEach((tile, tileIndex) => {
        const cell = findNextFreeCell(occupancy, columns, autoRow, autoCol);
        autoRow = cell.row;
        autoCol = cell.col;
        markOccupied(occupancy, autoRow, autoCol, 1);
        placements.push({
            el: tile,
            row: autoRow,
            col: autoCol,
            order: tileIndex
        });
        autoCol++;
        if (autoCol > columns) {
            autoRow++;
            autoCol = 1;
        }
    });

    return placements;
}

/**
 * Collect marketing slots that have an explicit grid row.
 * @param {HTMLElement} gridElContainer
 * @returns {{ slot: HTMLElement, order: number }[]}
 */
function getSlottedEntries(gridElContainer) {
    return (queryAll(SELECTOR_SLOT, gridElContainer) || []).reduce((acc, slot, index) => {
        const row = getExplicitGridRow(slot) || parseInt(slot.dataset.row, 10);
        if (Number.isFinite(row) && row > 0) {
            acc.push({ slot, order: index });
        }
        return acc;
    }, []);
}

/**
 * Reorder tiles + marketing slots in DOM to match visual grid.
 * Reparents slots out of wrappers (e.g. .pd-plp-grid-banners with display:contents).
 * @param {HTMLElement} gridElContainer
 * @param {{ el: HTMLElement, row: number, col: number, order: number }[]} placements
 */
function reorderMarketingSlotsInGrid(gridElContainer, placements) {
    if (!placements.length) {
        return;
    }

    const footer = queryFirst('.grid-footer', gridElContainer);
    const ordered = [...placements].sort((a, b) => a.row - b.row || a.col - b.col || a.order - b.order);
    const fragment = document.createDocumentFragment();

    ordered.forEach(({ el }) => {
        // Pull nested slots (pd-plp-grid-banners) up into the grid for ADA DOM order
        if (el.parentNode === gridElContainer || gridElContainer.contains(el)) {
            fragment.appendChild(el);
        }
    });

    if (footer && footer.parentNode === gridElContainer) {
        fragment.appendChild(footer);
    }

    gridElContainer.appendChild(fragment);
}

/**
 * Apply .m-start to tiles in column 1 — must run after slots are placed (same placements).
 * @param {HTMLElement} gridElContainer
 * @param {{ el: HTMLElement, row: number, col: number }[]} placements
 * @param {number} columns
 */
function applyRowStartClasses(gridElContainer, placements, columns) {
    const tiles = queryAll(SELECTOR_PRODUCT_TILE_CONTAINER, gridElContainer) || [];
    if (tiles.length) {
        removeClass(tiles, CLASS_ROW_START);
    }

    if (columns < 2 || !placements.length) {
        return;
    }

    placements.forEach(({ el, col }) => {
        if (col === 1 && hasClass(el, CLASS_PRODUCT_TILE_CONTAINER)) {
            addClass(el, CLASS_ROW_START);
        }
    });
}

/**
 * Clone Page Designer afterGrid region children into the product grid (before footer)
 * so slot positioning can place them with their data-row / grid-row.
 * Supports multiple .js-plp-after-grid sources (DOM order).
 * @param {HTMLElement} gridElContainer
 */
function injectPlpAfterGridContent(gridElContainer) {
    const sources = queryAll(SELECTOR_AFTER_GRID_SOURCE) || [];
    if (!gridElContainer || !sources.length) {
        return;
    }

    injectedAfterGridNodes.forEach(node => {
        [node, ...queryAll('.video-component', node)].forEach(el => {
            if (el._onResize) {
                window.removeEventListener('resize', el._onResize);
            }
        });
        node.remove();
    });
    injectedAfterGridNodes = [];

    const footer = queryFirst(SELECTOR_GRID_FOOTER, gridElContainer);
    sources.forEach(source => {
        Array.from(source.children).forEach(child => {
            const clone = child.cloneNode(true);
            // cloneNode copies classes, not listeners or IntersectionObserver targets
            if (hasClass(clone, 'video-component')) {
                removeClass(clone, 'initialized');
            }
            queryAll('.video-component.initialized', clone).forEach(el => {
                removeClass(el, 'initialized');
            });
            queryAll('video.lazy-video', clone).forEach(el => {
                removeClass(el, 'lz-pending');
            });
            if (footer) {
                footer.parentNode.insertBefore(clone, footer);
            } else {
                gridElContainer.appendChild(clone);
            }
            injectedAfterGridNodes.push(clone);
        });
    });
}

/**
 * Init Swiper for carousel wrappers cloned from afterGrid sources.
 */
function initInjectedAfterGridCarousels() {
    if (!injectedAfterGridNodes.length) {
        return;
    }

    const wrappers = [];

    injectedAfterGridNodes.forEach(node => {
        if (hasClass(node, 'js-carousel-wrapper') || hasClass(node, 'js-slot')) {
            wrappers.push(node);
        }
        queryAll('.js-carousel-wrapper, .js-slot', node).forEach(el => {
            wrappers.push(el);
        });
    });

    if (!wrappers.length) {
        return;
    }

    const { initCarousels } = require('../components/swiperCarousel').default;
    initCarousels(wrappers);
}

/**
 * Bind video controls on grid (ajax replace + after-grid clones).
 */
function initGridVideos(gridElContainer) {
    if (!gridElContainer) {
        return;
    }

    const video = require('../components/video').default;

    queryAll('.video-component', gridElContainer).forEach(el => {
        if (video.hasVideos(el)) {
            video.initVideos(el);
        }
    });
}

/**
 * Remove .m-initialized from the product grid (ajax start / before recalculation).
 * @param {HTMLElement|null|undefined} [gridElContainer]
 */
function clearProductGridInitialized(gridElContainer) {
    const grid = gridElContainer || queryFirst(SELECTOR_PRODUCT_GRID);
    if (grid) {
        removeClass(grid, CLASS_GRID_INITIALIZED);
    }
}

/**
 * Mark grid as ready after placement / m-start.
 * @param {HTMLElement} gridElContainer
 */
function markProductGridInitialized(gridElContainer) {
    if (gridElContainer) {
        addClass(gridElContainer, CLASS_GRID_INITIALIZED);
    }
}

/**
 * @param {HTMLElement|null|undefined} gridElContainer
 */
function refreshProductGridMarketingSlots(gridElContainer) {
    if (!gridElContainer) return;

    clearProductGridInitialized(gridElContainer);

    // 1) Inject PD after-grid clones
    injectPlpAfterGridContent(gridElContainer);

    const { columns } = updateGridState(gridElContainer);
    const tiles = queryAll(SELECTOR_PRODUCT_TILE_CONTAINER, gridElContainer) || [];
    const slots = getSlottedEntries(gridElContainer);

    // 2) One placement model (slots occupy cells, tiles fill the rest)
    const placements = computeVisualPlacements(tiles, slots, columns);

    // 3) Move slots/tiles into final DOM order
    if (slots.length) {
        reorderMarketingSlotsInGrid(gridElContainer, placements);
    }

    // 4) Last step — m-start from the placements we already know
    applyRowStartClasses(gridElContainer, placements, columns);

    initInjectedAfterGridCarousels();
    initGridVideos(gridElContainer);

    markProductGridInitialized(gridElContainer);

    document.body.dispatchEvent(new CustomEvent('plp:grid:ordered'));
}

/**
 * Re-run slot order / row modifiers when the column count changes (not on every pixel).
 */
function refreshIfLayoutChanged() {
    const gridElContainer = queryFirst(SELECTOR_PRODUCT_GRID);
    if (!gridElContainer) return;

    const currentWidth = window.innerWidth;
    const nextColumns = getColumnsByBreakpoint(currentWidth);
    const prevColumns = gridState.columns;

    if (prevColumns === nextColumns && gridState.width === currentWidth) {
        return;
    }

    gridState = {
        width: currentWidth,
        columns: nextColumns
    };

    // Column count changed → clear ready state, then full re-place + re-mark m-start
    if (prevColumns !== nextColumns) {
        clearProductGridInitialized(gridElContainer);
        refreshProductGridMarketingSlots(gridElContainer);
    }
}

module.exports = {
    refreshProductGridMarketingSlots,
    refreshIfLayoutChanged,
    clearProductGridInitialized,
    CLASS_GRID_INITIALIZED
};
